/**
 * Admin Analytics Hooks
 * Custom hooks using TanStack Query for data fetching
 */

import { useQuery, UseQueryResult } from '@tanstack/react-query';
import {
  PlanDistribution,
  UsageTrend,
  ConversionFunnelData,
  TopFeature,
  LimitExceededData,
  MRRTrendData,
  SubscriptionGrowthData,
  UserGrowthMetrics,
  DateRange,
  PromptStats,
  ContentAnalytics,
} from '@/types/admin/analytics';

// API base URL
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api';

// Generic fetch with auth
const apiFetch = async (url: string) => {
  const response = await fetch(`${API_BASE_URL}${url}`, {
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Unknown error' }));
    throw new Error(error.message || 'API request failed');
  }

  const json = await response.json();
  return json.data;
};

/**
 * Get subscription plan distribution
 * @param days Number of days to look back (optional, undefined = all time)
 */
export const usePlanDistribution = (days?: number): UseQueryResult<PlanDistribution[]> => {
  return useQuery({
    queryKey: ['admin', 'analytics', 'plan-distribution', days],
    queryFn: () => apiFetch(days ? `/admin/analytics/plan-distribution?days=${days}` : '/admin/analytics/plan-distribution'),
    refetchInterval: 300000, // 5 min
    staleTime: 240000, // 4 min
    retry: 2,
  });
};

/**
 * Get usage trends over time
 */
export const useUsageTrends = (dateRange: DateRange): UseQueryResult<UsageTrend[]> => {
  return useQuery({
    queryKey: ['admin', 'analytics', 'usage-trends', dateRange],
    queryFn: () =>
      apiFetch(
        `/admin/analytics/usage-trends?start=${dateRange.start}&end=${dateRange.end}`
      ),
    refetchInterval: 300000,
    staleTime: 240000,
    retry: 2,
  });
};

/**
 * Get subscription plan conversion funnel
 * @param days Number of days to look back (optional, null = all time)
 */
export const useConversionFunnel = (days?: number): UseQueryResult<ConversionFunnelData[]> => {
  return useQuery({
    queryKey: ['admin', 'analytics', 'conversion-funnel', days],
    queryFn: () => apiFetch(days ? `/admin/analytics/conversion-funnel?days=${days}` : '/admin/analytics/conversion-funnel'),
    refetchInterval: 300000,
    staleTime: 240000,
    retry: 2,
  });
};

/**
 * Get most used features
 * @param days Number of days to look back (optional, null = all time)
 */
export const useTopFeatures = (days?: number): UseQueryResult<TopFeature[]> => {
  return useQuery({
    queryKey: ['admin', 'analytics', 'top-features', days],
    queryFn: () => apiFetch(days ? `/admin/analytics/top-features?days=${days}` : '/admin/analytics/top-features'),
    refetchInterval: 300000,
    staleTime: 240000,
    retry: 2,
  });
};

/**
 * Get limit exceeded events
 */
export const useLimitExceededEvents = (
  days: number = 7
): UseQueryResult<LimitExceededData> => {
  return useQuery({
    queryKey: ['admin', 'analytics', 'limit-exceeded', days],
    queryFn: () => apiFetch(`/admin/analytics/limit-exceeded-events?days=${days}`),
    refetchInterval: 300000,
    staleTime: 240000,
    retry: 2,
  });
};

/**
 * Get MRR trend
 */
export const useMRRTrend = (days: number = 90): UseQueryResult<MRRTrendData[]> => {
  return useQuery({
    queryKey: ['admin', 'analytics', 'mrr-trend', days],
    queryFn: () => apiFetch(`/admin/analytics/mrr-trend?days=${days}`),
    refetchInterval: 300000,
    staleTime: 240000,
    retry: 2,
  });
};

/**
 * Get subscription growth
 */
export const useSubscriptionGrowth = (
  dateRange: DateRange
): UseQueryResult<SubscriptionGrowthData[]> => {
  return useQuery({
    queryKey: ['admin', 'analytics', 'subscription-growth', dateRange],
    queryFn: () =>
      apiFetch(
        `/admin/analytics/subscription-growth?start=${dateRange.start}&end=${dateRange.end}`
      ),
    refetchInterval: 300000,
    staleTime: 240000,
    retry: 2,
  });
};

/**
 * Get user growth metrics
 */
export const useUserGrowth = (): UseQueryResult<UserGrowthMetrics> => {
  return useQuery({
    queryKey: ['admin', 'analytics', 'user-growth'],
    queryFn: () => apiFetch('/admin/analytics/user-growth'),
    refetchInterval: 300000,
    staleTime: 240000,
    retry: 2,
  });
};

/**
 * Get prompt statistics from the prompts table (REAL DATA)
 * @param days Number of days to look back (optional, undefined = all time)
 */
export const usePromptStats = (days?: number): UseQueryResult<PromptStats> => {
  return useQuery({
    queryKey: ['admin', 'analytics', 'prompt-stats', days],
    queryFn: () => apiFetch(days ? `/admin/analytics/prompt-stats?days=${days}` : '/admin/analytics/prompt-stats'),
    refetchInterval: 300000,
    staleTime: 240000,
    retry: 2,
  });
};

/**
 * Get content analytics from actual data tables (prompts, collections, comments, votes)
 * This gives REAL counts instead of usage_counters
 * @param days Number of days to look back (optional, undefined = all time)
 */
export const useContentAnalytics = (days?: number): UseQueryResult<ContentAnalytics[]> => {
  return useQuery({
    queryKey: ['admin', 'analytics', 'content-analytics', days],
    queryFn: () => apiFetch(days ? `/admin/analytics/content-analytics?days=${days}` : '/admin/analytics/content-analytics'),
    refetchInterval: 300000,
    staleTime: 240000,
    retry: 2,
  });
};

/**
 * Get content trends over time from REAL data tables (prompts, collections, comments, votes)
 * This replaces usage-trends with actual data
 */
export const useContentTrends = (dateRange: DateRange): UseQueryResult<UsageTrend[]> => {
  return useQuery({
    queryKey: ['admin', 'analytics', 'content-trends', dateRange],
    queryFn: () =>
      apiFetch(
        `/admin/analytics/content-trends?start=${dateRange.start}&end=${dateRange.end}`
      ),
    refetchInterval: 300000,
    staleTime: 240000,
    retry: 2,
  });
};
